import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, User, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import apiClient from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import { CustomerSummaryDto } from '../types/dtos';
import { resolveUrl } from '../utils/urlUtils';
import { exportToCsv } from '../utils/csvHelper';

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleExportCustomers = () => {
    exportToCsv('xtrem_customers_export', customers, {
      id: 'Customer ID',
      fullName: 'Full Name',
      email: 'Email',
      status: 'Status',
      createdAt: 'Registered Date'
    });
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await apiClient.get<CustomerSummaryDto[]>('/admin/customers');
        setCustomers(res.data);
      } catch (error: any) {
        console.error("Failed to fetch customers", error);
        setError(error.response?.data?.message || "Failed to load customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  // Client-side search filtering (or pass to API)
  const filteredCustomers = customers.filter(c =>
    c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Verified': return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><CheckCircle size={12} /> Verified</span>;
      case 'New': return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><User size={12} /> New</span>;
      case 'Rejected': return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><XCircle size={12} /> Rejected</span>;
      default: return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><Clock size={12} /> {status}</span>;
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto">

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Customers</h1>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-full shadow-sm"
                />
              </div>
              {customers.length > 0 && (
                <button
                  onClick={handleExportCustomers}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-sm active:scale-95 shrink-0"
                  aria-label="Export Customers as CSV"
                >
                  <FileText size={16} />
                  <span className="hidden sm:inline">Export CSV</span>
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 flex justify-center"><LoadingSpinner /></div>
            ) : error ? (
              <div className="p-12 text-center text-red-600 font-medium whitespace-pre-wrap">
                {error}
              </div>
            ) : (
              <div className="bg-white">
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                      <tr>
                        <th className="px-6 py-4">Name</th>
                        <th className="px-6 py-4">Contact</th>
                        <th className="px-6 py-4">KYC Status</th>
                        <th className="px-6 py-4">Registered</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredCustomers.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 border border-gray-100">
                                {c.profileImageBase64 || c.profileImageUrl ? (
                                  <img
                                    src={c.profileImageBase64 || resolveUrl(c.profileImageUrl) || ''}
                                    alt={c.fullName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center text-gray-400">
                                    <User size={20} />
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{c.fullName}</div>
                                <div className="text-xs text-gray-500">{c.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{c.phoneNumber}</td>
                          <td className="px-6 py-4">{getStatusBadge(c.kycStatus)}</td>
                          <td className="px-6 py-4 text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => navigate(`/admin/customers/${c.id}`)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center justify-end gap-1"
                            >
                              <Eye size={16} /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-800">
                  {filteredCustomers.map((c) => (
                    <div key={c.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-850 transition-colors">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden border border-gray-100 dark:border-slate-800">
                            {c.profileImageBase64 || c.profileImageUrl ? (
                              <img
                                src={c.profileImageBase64 || resolveUrl(c.profileImageUrl) || ''}
                                alt={c.fullName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                                <User size={24} />
                              </div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{c.fullName}</h3>
                            <p className="text-xs text-gray-500 dark:text-slate-400">{c.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/customers/${c.id}`)}
                          className="p-2 text-blue-600 bg-blue-50 dark:bg-blue-950/40 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                        >
                          <Eye size={18} />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 dark:bg-slate-900/40 border dark:border-slate-800/80 rounded-lg">
                        <div>
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-1">KYC Status</p>
                          {getStatusBadge(c.kycStatus)}
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-1">Contact</p>
                          <p className="text-xs text-gray-600 dark:text-slate-300 font-medium">{c.phoneNumber}</p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center text-[10px] text-gray-400 dark:text-slate-500">
                        <Clock size={12} className="mr-1" />
                        <span>Registered {new Date(c.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}

